/**
 * landingArchetypeContent — rich, deep content for each freelance archetype
 * landing page (/for/:slug).
 *
 * Central source of truth: keeps copy in one place so non-engineers can
 * tweak headlines, pain points and benefits without touching layout code.
 *
 * Structure per archetype:
 *   - slug:       URL path segment (English, kebab-case)
 *   - key:        legacy key matching LandingArchetypes (icon mapping)
 *   - who:        persona label localized
 *   - tag:        short positioning tag
 *   - heroTitle:  H1 — outcome-first
 *   - heroSub:    one-paragraph empathetic hook
 *   - pains:      [{title, body}]  3 concrete pain points
 *   - gains:      [{title, body}]  3 outcomes Lumfi delivers
 *   - features:   [{icon, visual, title, body}] 4 product features mapped to this persona
 *                 `visual` matches a kind in ArchetypeFeatureVisual.jsx
 *   - workflow:   [{step, title, body}] 3-4 daily/weekly usage steps
 *   - quote:      a synthesised "voice of the persona" line (italic, no fake attribution)
 *   - faqs:       [{q,a}] 3-4 persona-specific FAQs
 *   - cta:        single CTA microcopy
 */

const ES = [
  {
    slug: "developer",
    key: "dev",
    who: "Desarrollador/a freelance",
    tag: "Cross-currency",
    heroTitle: "Cobra en USD. Vive en local. Mira tu plata real sin abrir tres apps.",
    heroSub: "Si facturas a clientes del exterior y pagas alquiler, tarjetas y proveedores en moneda local, tu cash real cambia cada día. Lumfi te lo dice en un solo número.",
    pains: [
      { title: "Tu “saldo” miente", body: "El banco muestra un número, pero entre el FX, el impuesto y los cobros que aún no llegan, no sabes qué es realmente tuyo." },
      { title: "Excel + Notion + 3 cuentas", body: "Pasas horas el lunes consolidando cuántos USD vienen y qué tasa usar. Cuando terminas, ya es martes." },
      { title: "Cliente paga, banco demora", body: "Wise, Payoneer, transferencia directa. Cada uno con su tiempo. Tu flujo se distorsiona y no te das cuenta hasta que es tarde." },
    ],
    gains: [
      { title: "Un número, dos monedas", body: "Lumfi te muestra tu cash en USD y en moneda local con FX automático y al día." },
      { title: "Cobros con probabilidad", body: "Cada receivable lleva un % de confianza. Lumfi pondera y te dice cuánto cuentas de verdad para este mes." },
      { title: "Bot de Telegram para registrar", body: "“Me llegaron 3.500 USD del cliente X”. Listo. Conversión, cuenta y categoría hechas." },
    ],
    features: [
      { icon: "Globe", visual: "fx-multi", title: "FX automático multi-moneda", body: "Tasas reales actualizadas. Configura una cuenta en USD y otra en COP/MXN/ARS — Lumfi consolida todo en tu moneda principal." },
      { icon: "Receipt", visual: "receivables-list", title: "Receivables con probabilidad", body: "Asigna 95% a un cliente confiable y 60% a uno nuevo. Tu pronóstico ya no es optimista, es honesto." },
      { icon: "Bot", visual: "bot-chat", title: "Bot de Telegram", body: "Registra ingresos y gastos por chat. El bot entiende lenguaje natural y categoriza por ti." },
      { icon: "AlertCircle", visual: "alert-gauge", title: "Alerta de límite seguro", body: "Antes de comprar el monitor de 800 USD, Lumfi te avisa si vas bien o si comprometes la quincena." },
    ],
    workflow: [
      { step: "01", title: "Conecta tus cuentas (manual)", body: "Cuenta USD (Wise/Payoneer), cuenta local, tarjeta de crédito. 2 minutos." },
      { step: "02", title: "Carga tus contratos como receivables", body: "Cliente X: 3.000 USD mensual, día 5. Cliente Y: proyecto cerrado de 8.000 USD en 3 pagos. Lumfi pondera por probabilidad." },
      { step: "03", title: "Mira tu safe-to-use", body: "Lumfi cruza cash + receivables × probabilidad − pagos comprometidos. Te da un solo número por semana." },
    ],
    quote: "“Cuando vivo entre Stripe, Wise y mi cuenta del banco local, dejar el pilotaje a una sola pantalla es lo único que me devuelve el tiempo del lunes.”",
    faqs: [
      { q: "¿Lumfi se conecta automáticamente con Wise/Payoneer?", a: "No. Lumfi es manual o vía bot, justamente para que tu cash esté donde tú decides. Registras el ingreso una vez y Lumfi hace toda la conversión y proyección." },
      { q: "¿Qué tasa de cambio usa?", a: "Tasas de mercado del día (BCRA/Banrep/Banxico según país). Puedes sobreescribir la tasa por movimiento si tu liquidación llegó con otra." },
      { q: "¿Sirve si trabajo para una sola empresa?", a: "Sí, especialmente. Lumfi te muestra qué pasa si esa empresa atrasa el pago un mes y cuánto reserva fiscal tienes que apartar cada cobro." },
    ],
    cta: "Probar Lumfi con mi cash real",
  },
  {
    slug: "designer",
    key: "design",
    who: "Diseñador/a UX/UI",
    tag: "Proyectos largos",
    heroTitle: "Anticipo, hito, cierre. Vive sin la montaña rusa entre cobros.",
    heroSub: "Tus proyectos duran 2-4 meses. El mes que cobras el cierre te sientes rico, al siguiente te ahogas. Lumfi reparte tu cash mental para que no oscile.",
    pains: [
      { title: "Mes gordo / mes flaco", body: "Cobraste 12K el viernes. ¿Te lo gastas o ese dinero ya es de los próximos dos meses sin cobro?" },
      { title: "Anticipos en limbo", body: "Te firmaron el contrato, pero el anticipo aún no llega. ¿Cuento con esa plata o no?" },
      { title: "Los retoques eternos", body: "El proyecto se estiró un mes más. El cliente paga al final. Tu calendario de cash se rompió." },
    ],
    gains: [
      { title: "Cash repartido por semana", body: "Lumfi divide tus cobros por las semanas que vienen y te da un límite seguro semanal, no un saldo engañoso." },
      { title: "Hitos como receivables recurrentes", body: "Carga el contrato como 3 pagos (anticipo, mid, cierre) y Lumfi proyecta cada uno con probabilidad." },
      { title: "Alertas si te estás pasando", body: "Cuando el proyecto se atrasa o gastas más de la cuenta, Lumfi te avisa con tiempo." },
    ],
    features: [
      { icon: "LineChart", visual: "weekly-chart", title: "Cash-flow semanal", body: "Olvídate del “tengo 10K en el banco”. Lumfi te dice cuánto tienes que durar y cuánto te toca por semana." },
      { icon: "Receipt", visual: "timeline", title: "Hitos de proyecto", body: "Define proyecto = cliente + 3 pagos con probabilidad. Lumfi proyecta cada hito como un cobro futuro." },
      { icon: "AlertCircle", visual: "alert-gauge", title: "Límite seguro semanal", body: "Tu “safe-to-use” se recalcula cada vez que un hito se confirma o se posterga." },
      { icon: "Target", visual: "goal-ring", title: "Goal de “mes flaco”", body: "Reserva automática para los meses que no cobras un cierre. Lumfi calcula cuánto debes apartar." },
    ],
    workflow: [
      { step: "01", title: "Carga el proyecto completo", body: "Cliente, monto total, 3 fechas de cobro y probabilidad de cada una." },
      { step: "02", title: "Lumfi reparte por semana", body: "Si el proyecto dura 3 meses, Lumfi divide el cash en 12 semanas. Sabes qué te toca esta semana." },
      { step: "03", title: "Cuando se atrasa, ajustas", body: "Mueves la fecha del hito y el plan se recalcula en segundos." },
    ],
    quote: "“Cobrar 4 millones el viernes y tener 3 meses sin cobro hasta el siguiente cierre es la trampa del freelance. Lumfi me reparte ese cash en mi cabeza.”",
    faqs: [
      { q: "¿Y si el cliente nunca paga el cierre?", a: "Cada hito tiene un % de probabilidad. Si baja la confianza, Lumfi recalcula tu cash real sin que tengas que rehacer el archivo." },
      { q: "¿Puedo separar varios proyectos?", a: "Sí. Cada proyecto puede ser un “cliente recurrente” en Lumfi, con sus propios hitos, fechas y probabilidad." },
      { q: "¿Cómo manejo gastos del proyecto (assets, fonts, plugins)?", a: "Los registras como pagos comprometidos del proyecto. Lumfi los descuenta antes de mostrarte tu safe-to-use." },
    ],
    cta: "Repartir mi próximo proyecto en Lumfi",
  },
  {
    slug: "content-creator",
    key: "content",
    who: "Content creator",
    tag: "Multi-fuente",
    heroTitle: "AdSense, marcas, suscripciones, eventos. Un solo número que entiendes.",
    heroSub: "Cada plataforma paga distinto día, en distinta moneda y con distinta retención. Lumfi te unifica el cash real sin pestañas abiertas.",
    pains: [
      { title: "5 fuentes, 5 calendarios", body: "YouTube paga el 21. Marca paga al neto 60. Patreon es semanal. Te pierdes." },
      { title: "Brand deals impredecibles", body: "Te firman tres este mes y ninguno el próximo. ¿Cómo distribuyes ese cash?" },
      { title: "Retenciones e impuestos", body: "AdSense te retiene 30%. Marcas a veces con factura, a veces sin. No sabes cuánto es realmente tuyo." },
    ],
    gains: [
      { title: "Todas las fuentes consolidadas", body: "AdSense, marcas, Patreon, eventos. Cada una con su día de pago, su moneda y su retención." },
      { title: "Calendario de cobros visual", body: "Mira mes a mes qué te llega y qué tan probable es." },
      { title: "Resumen semanal por WhatsApp", body: "Cada lunes Lumfi te manda un mensaje: cuánto ganas esta semana, qué se viene, qué se atrasa." },
    ],
    features: [
      { icon: "Coins", visual: "source-split", title: "Múltiples fuentes de ingreso", body: "Crea “cuentas” por plataforma: YouTube, Patreon, Marca-X. Lumfi muestra el split y el total." },
      { icon: "Receipt", visual: "receivables-list", title: "Brand deals con probabilidad", body: "Carga un acuerdo aún no firmado al 60%. Si se cae, no se descalabra tu plan." },
      { icon: "MessageCircle", visual: "whatsapp-digest", title: "Resumen WhatsApp", body: "Mensaje semanal con tu cash + receivables + alerta de impuestos. Lo lees mientras desayunas." },
      { icon: "AlertCircle", visual: "reserve-chip", title: "Reserva fiscal automática", body: "Por cada cobro, Lumfi separa un % a una “cuenta de impuestos” mental. Cuando llega abril no lloras." },
    ],
    workflow: [
      { step: "01", title: "Una cuenta por fuente", body: "YouTube, marcas, Patreon, eventos. Cada una con su día típico de cobro." },
      { step: "02", title: "Carga el brand deal en cuanto firmas", body: "Marca-X: 5.000 USD, neto 60 días, 80% probabilidad. Lumfi ya lo proyecta." },
      { step: "03", title: "Recibe el resumen los lunes", body: "WhatsApp/Email con tu safe-to-use, próximos cobros y un “heads-up” si hay riesgo." },
    ],
    quote: "“Si mi YouTube y mi Patreon pagan en distinta semana y mi marca de ropa paga a 60 días, tener un solo número los lunes vale más que cualquier dashboard bonito.”",
    faqs: [
      { q: "¿Lumfi entiende ingresos pasivos?", a: "Sí. Puedes crear ingresos “recurrentes” con monto estimado y Lumfi los proyecta con un rango de probabilidad." },
      { q: "¿Sirve si gano 200 dólares al mes en YouTube?", a: "Sí — el plan gratuito te alcanza. Lumfi se hace útil cuando hay más de una fuente, no por monto." },
      { q: "¿Manda alertas si una marca atrasa?", a: "Sí, cuando la fecha de cobro pasa sin confirmar, Lumfi te lo marca y recalcula tu cash sin esa plata." },
    ],
    cta: "Unificar mis fuentes en Lumfi",
  },
  {
    slug: "consultant",
    key: "consult",
    who: "Consultor/a independiente",
    tag: "Retainer + spot",
    heroTitle: "Retainer + proyectos. ¿Qué pasa si el retainer se acaba?",
    heroSub: "Vives de un retainer mensual estable + proyectos sueltos. Pero el día que el cliente del retainer se va, ¿cuánto colchón tienes? Lumfi te lo dice antes.",
    pains: [
      { title: "El retainer es tu base", body: "Lo das por sentado. Pero ese cliente es un solo email de distancia de irse." },
      { title: "Proyectos spot son random", body: "Aceptas porque vienen, no porque lo necesites. Y luego se acumulan en el mismo mes." },
      { title: "No tienes plan B numérico", body: "Sabes que “estarías bien un par de meses”. Pero ¿cuántos exactamente?" },
    ],
    gains: [
      { title: "Escenarios what-if", body: "“¿Y si pierdo el retainer en marzo?” Lumfi te muestra los próximos 90 días sin esa plata." },
      { title: "Recurring vs spot", body: "Separa ambos. Sabes qué es ingreso estable y qué es bono extra." },
      { title: "Reportes para tu contador", body: "Exporta por proyecto, mes o cliente. Sin Excel intermedios." },
    ],
    features: [
      { icon: "LineChart", visual: "scenario-whatif", title: "Escenarios what-if", body: "Modela la pérdida del retainer, el atraso de un proyecto o un gasto inesperado y mira el impacto a 90 días." },
      { icon: "Coins", visual: "source-split", title: "Income recurrente vs spot", body: "Lumfi etiqueta cada ingreso y te dice qué % de tu cash depende de un solo cliente." },
      { icon: "Receipt", visual: "receivables-list", title: "Receivables por cliente", body: "Cada cliente con sus pagos, fechas, probabilidad. Sabes cuál es el más valioso y el más frágil." },
      { icon: "Mail", visual: "export-rows", title: "Exportes para contador", body: "CSV, XLSX o PDF por mes/cliente. Tu contador deja de pedirte “el resumen de junio”." },
    ],
    workflow: [
      { step: "01", title: "Marca el retainer como recurrente", body: "Cliente X, 4.000 USD/mes, día 5, 95% probabilidad. Es tu piso." },
      { step: "02", title: "Agrega los proyectos spot", body: "Cuando aceptas uno, lo cargas con sus hitos. Lumfi te dice si lo necesitas o no." },
      { step: "03", title: "Corre el escenario", body: "En settings → escenarios, simula la pérdida del retainer. Lumfi recalcula tu pista de aterrizaje." },
    ],
    quote: "“La pregunta nunca es ‘cuánto gano’. Es ‘cuánto duro si esto se cae’. Lumfi me responde eso en 3 segundos.”",
    faqs: [
      { q: "¿Lumfi puede modelar más de un escenario?", a: "Sí. Puedes guardar varios (“sin retainer”, “con bono extra”, “mes flaco”) y compararlos lado a lado." },
      { q: "¿Funciona si soy consultor solo o tengo un equipo chico?", a: "Ambos. Para equipos hay workspaces y roles — el contador, tu socio, tu asistente cada uno con su nivel de acceso." },
      { q: "¿Genera factura?", a: "Aún no. Lumfi se enfoca en cash-flow. Conectamos exportes con tu sistema de facturación." },
    ],
    cta: "Probar un escenario en Lumfi",
  },
  {
    slug: "lawyer-accountant",
    key: "legal",
    who: "Abogado/a o contador/a",
    tag: "Cíclico",
    heroTitle: "Honorarios cíclicos. Meses gordos y meses flacos. Cero sorpresas.",
    heroSub: "Tus clientes pagan al cierre del mes, al final del trimestre o cuando salen los honorarios judiciales. Lumfi te enseña a vivir el flaco con la plata del gordo.",
    pains: [
      { title: "Cobros bimestrales o trimestrales", body: "Pasan dos meses sin ingreso y de pronto llega todo junto. Cuesta repartir." },
      { title: "Honorarios judiciales impredecibles", body: "Sale cuando sale. Tienes que vivir como si no fuera a salir nunca." },
      { title: "Gastos fijos altos", body: "Oficina, asistente, suscripciones legales. El cash debe alcanzar siempre, no “a veces”." },
    ],
    gains: [
      { title: "Proyección a 3 meses", body: "Mira el mes flaco antes de que llegue. Lumfi te dice cuánto del cobro actual debes guardar." },
      { title: "Cobros estacionales", body: "Marca un honorario como “probable Q2” sin fecha exacta y Lumfi lo proyecta con probabilidad." },
      { title: "Exportes para conciliación", body: "Listo para tu contador o socio. Por cliente, por expediente, por mes." },
    ],
    features: [
      { icon: "LineChart", visual: "forecast-chart", title: "Forecast 90 días", body: "Lumfi simula tus próximos 3 meses incluyendo gastos fijos. Ves la curva, no solo el saldo." },
      { icon: "Receipt", visual: "receivables-list", title: "Cobros estacionales", body: "Define ingresos sin fecha exacta — Q2 o “cuando salga el fallo” — con probabilidad y monto rango." },
      { icon: "Coins", visual: "reserve-chip", title: "Reserva por mes flaco", body: "Lumfi aparta automáticamente una % de cada cobro grande para los meses sin ingreso." },
      { icon: "Mail", visual: "export-rows", title: "Exportes profesionales", body: "Reportes mensuales por cliente y expediente, listos para conciliación." },
    ],
    workflow: [
      { step: "01", title: "Carga tus expedientes activos", body: "Cliente, monto estimado, fecha probable (mes o trimestre) y % de confianza." },
      { step: "02", title: "Define tus fijos", body: "Oficina, asistente, software jurídico, impuestos trimestrales. Lumfi los descuenta antes." },
      { step: "03", title: "Mira la curva, no el saldo", body: "Tu “línea de aterrizaje” a 90 días te muestra los puntos críticos antes de que te alcancen." },
    ],
    quote: "“Cuando vivís de honorarios judiciales, la pregunta no es cuánto cobras este mes. Es cuánto puedes gastar este mes sin pedirle al estudio.”",
    faqs: [
      { q: "¿Maneja IVA / retenciones?", a: "Sí. Por cada ingreso puedes marcar el % de retención e impuesto. Lumfi descuenta y aparta automáticamente." },
      { q: "¿Sirve para un estudio chico (2-3 socios)?", a: "Sí, vía workspaces. Cada socio puede ver su tabla y el total compartido del estudio." },
      { q: "¿Puedo compartir un link de solo-lectura con mi contador?", a: "Sí. Lumfi genera un link público de solo lectura para que tu contador acceda al cash-flow sin login." },
    ],
    cta: "Proyectar mis honorarios con Lumfi",
  },
  {
    slug: "coach-tutor",
    key: "coach",
    who: "Coach o tutor/a online",
    tag: "Suscripciones",
    heroTitle: "Suscripciones que entran y salen. Mira tu MRR real, no el bruto.",
    heroSub: "Vendes mensualidades o paquetes. La gente se suscribe, se da de baja, vuelve. Lumfi te muestra el ingreso recurrente real, no el espejismo.",
    pains: [
      { title: "Bajas silenciosas", body: "Tres clientes se dieron de baja este mes pero no lo notaste hasta el cierre." },
      { title: "MRR vs. ingreso real", body: "Stripe te muestra el MRR pero descuenta tarjeta, no IVA, no devoluciones. Tu cash real es otro." },
      { title: "Paquetes vs. mensualidades", body: "Mezclas paquetes anuales y mensualidades. Diferenciar el flujo es un dolor." },
    ],
    gains: [
      { title: "MRR proyectado real", body: "Lumfi suma tus suscripciones activas, descuenta comisiones y FX, y te da el número que cuenta." },
      { title: "Métricas de retención", body: "Mira cuánto duran tus clientes en promedio y predice tu churn." },
      { title: "Alertas de bajas", body: "Cuando un cliente cancela, Lumfi te avisa y recalcula tu cash de los próximos 3 meses." },
    ],
    features: [
      { icon: "TrendingUp", visual: "mrr-metric", title: "MRR proyectado", body: "No el bruto de Stripe — el MRR neto que llega a tu cuenta después de fees, FX e impuestos." },
      { icon: "Receipt", visual: "receivables-list", title: "Suscripciones como receivables", body: "Cada cliente activo es un cobro recurrente con su probabilidad de retención." },
      { icon: "AlertCircle", visual: "churn-alert", title: "Alertas de churn", body: "Cuando un cliente cancela, Lumfi te avisa y muestra el impacto a 3 meses." },
      { icon: "Target", visual: "goal-ring", title: "Goal de MRR estable", body: "Define un MRR mínimo (tu “base”) y Lumfi te avisa cuando bajas o subes el umbral." },
    ],
    workflow: [
      { step: "01", title: "Carga tus suscripciones activas", body: "Cliente, monto mensual o anual, fecha de inicio, probabilidad de retención (90% por defecto)." },
      { step: "02", title: "Marca paquetes vs. mensualidades", body: "Lumfi los proyecta distinto: paquete anual no recurre, mensualidad sí." },
      { step: "03", title: "Mira tu MRR honesto", body: "El número que aparece es el que llega a tu cuenta, no el que Stripe te muestra." },
    ],
    quote: "“Stripe dice 8K MRR y yo gano 5K después de fees, FX y un par de devoluciones. Lumfi me muestra el 5K, el número real.”",
    faqs: [
      { q: "¿Se conecta con Stripe automáticamente?", a: "No por ahora. Cargas las suscripciones manualmente o desde un CSV de Stripe. Lumfi proyecta desde ahí." },
      { q: "¿Funciona si vendo cursos one-shot?", a: "Sí. Lo cargas como ingreso único y Lumfi lo descuenta de tu flujo del mes." },
      { q: "¿Predice mi churn?", a: "Con base en tu historial de bajas, Lumfi te muestra una estimación. No es magia — es promedio móvil de los últimos 6 meses." },
    ],
    cta: "Calcular mi MRR real con Lumfi",
  },
  {
    slug: "ecommerce",
    key: "ecom",
    who: "E-commerce solo",
    tag: "Inventory gap",
    heroTitle: "Mercado Libre libera pasado mañana. Tu proveedor pide hoy. ¿Cómo lo cuadras?",
    heroSub: "Cobras cuando ML/Shopify liberan. Pero el proveedor pide la mitad antes. Esa diferencia de tiempo puede romperte. Lumfi la pone visible.",
    pains: [
      { title: "Cobros con retención", body: "ML libera 14 días después. ¿Cuánto tienes hoy disponible vs. comprometido?" },
      { title: "Proveedores antes que ventas", body: "Pagas el stock antes de venderlo. Si no calculas bien, te quedas sin caja a mitad de mes." },
      { title: "Varias plataformas", body: "ML + Shopify + tienda física + Instagram. Cada una con su cuenta, su comisión, su día." },
    ],
    gains: [
      { title: "Cuentas separadas por plataforma", body: "Lumfi te muestra el cash de cada canal por separado y el consolidado." },
      { title: "Pagos comprometidos a futuro", body: "Proveedor pide a 30 días: Lumfi lo descuenta antes de mostrarte tu safe-to-use." },
      { title: "Cash gap semana a semana", body: "Mira si la próxima semana se queda sin oxígeno antes de que pase." },
    ],
    features: [
      { icon: "Coins", visual: "platform-accounts", title: "Cuentas por plataforma", body: "ML, Shopify, tienda física. Cada una con su saldo, su comisión y su día de liberación." },
      { icon: "Receipt", visual: "committed-payments", title: "Pagos a futuro", body: "Carga compromisos con proveedor a 30/60/90 días. Lumfi los muestra como “cash comprometido” no como “tu plata”." },
      { icon: "LineChart", visual: "gap-weekly", title: "Cash gap semanal", body: "Lumfi proyecta semana a semana y te avisa si una de las próximas 4 se queda corta." },
      { icon: "AlertCircle", visual: "alert-gauge", title: "Alertas pre-bache", body: "Cuando el gap supera un umbral que tú defines, Lumfi te avisa con tiempo de mover stock o pagos." },
    ],
    workflow: [
      { step: "01", title: "Una cuenta por canal", body: "ML disponible, ML reservado (no liberado aún), Shopify, tienda física, Instagram." },
      { step: "02", title: "Carga compromisos a futuro", body: "Pago al proveedor: 4 millones a 30 días. Pago de bodega: 800 mil mensual. Lumfi los proyecta." },
      { step: "03", title: "Mira el gap por semana", body: "Lumfi te dice cuál es la semana más débil de los próximos 30 días y por cuánto." },
    ],
    quote: "“Vendí 50 millones en el mes pero terminé apretado porque la mitad estaba bloqueada en ML. Saberlo antes habría cambiado todo.”",
    faqs: [
      { q: "¿Lumfi se conecta con ML / Shopify?", a: "Aún no — registras los cobros manualmente o por CSV. La razón: tu cash real depende de qué tan rápido ML libera, no de la venta bruta." },
      { q: "¿Maneja IVA del producto?", a: "Sí. Por cada venta puedes restar el % de IVA + comisión de plataforma para ver el ingreso neto." },
      { q: "¿Sirve si solo vendo por Instagram?", a: "Sí. Cualquier canal con cobros y compromisos vale — Lumfi se enfoca en el flujo, no en el conector." },
    ],
    cta: "Cerrar mi gap con Lumfi",
  },
  {
    slug: "photographer",
    key: "photo",
    who: "Fotógrafo/a o eventos",
    tag: "Estacional",
    heroTitle: "Tu temporada son 5 meses. Tienes que vivir 12 con esa plata.",
    heroSub: "Bodas y eventos viven noviembre-marzo. Abril-octubre estás en mínimos. Lumfi te enseña cuánto repartir y cuándo gastarlo.",
    pains: [
      { title: "Temporada alta engaña", body: "Cobras tres bodas en diciembre y crees que vas como rey. Después viene mayo." },
      { title: "Equipo cuesta plata", body: "Lentes, cámaras, drones, repuestos. Inviertes en alta para que rinda en alta… si te queda algo." },
      { title: "Anticipos vs. saldos", body: "Cada evento es 30% al firmar + 70% al cierre. Cómo proyectar eso sin Excel es difícil." },
    ],
    gains: [
      { title: "Reparto por meses bajos", body: "Lumfi calcula cuánto debes apartar de cada evento para vivir los 7 meses sin trabajo intenso." },
      { title: "Reserva fiscal automática", body: "Por cada cobro grande, Lumfi separa el % de impuesto. Cuando llega abril no lloras." },
      { title: "Goals de ahorro estacional", body: "Define cuánto necesitas para llegar a octubre. Lumfi te muestra si vas bien o no." },
    ],
    features: [
      { icon: "PiggyBank", visual: "monthly-allocation", title: "Reparto por mes", body: "Lumfi suma tu ingreso anual estimado y lo divide en 12. Sabes cuánto te toca mensual “teórico”." },
      { icon: "Receipt", visual: "milestone-payments", title: "Anticipos + saldos", body: "Carga cada evento como dos cobros (anticipo + saldo) con sus fechas y probabilidad." },
      { icon: "AlertCircle", visual: "reserve-chip", title: "Reserva fiscal auto", body: "Define un % (ej. 25%) y Lumfi lo aparta de cada cobro grande automáticamente." },
      { icon: "Target", visual: "goal-ring", title: "Goal estacional", body: "Meta: 8M para octubre. Lumfi te dice cuánto de cada evento aportar y si vas adelante o atrás." },
    ],
    workflow: [
      { step: "01", title: "Carga tu agenda de la temporada", body: "Cada evento con fecha, monto total, 30% anticipo y 70% saldo el día del evento." },
      { step: "02", title: "Define tu meta de mes flaco", body: "“Quiero 1.5M de cash en abril.” Lumfi calcula el reparto que necesitas hacer." },
      { step: "03", title: "Mira el % de avance", body: "Cada vez que cobras un anticipo, Lumfi ajusta el avance de tu meta de octubre." },
    ],
    quote: "“Bodas en diciembre, bautizos en enero, y de marzo a octubre nada. Si no aparto, me como toda la temporada en febrero. Lumfi me reparte la cabeza.”",
    faqs: [
      { q: "¿Sirve si hago corporativo además de eventos?", a: "Sí — puedes mezclar fuentes estacionales con recurrentes (corporativo, banco de fotos, etc.) en el mismo workspace." },
      { q: "¿Calcula amortización de cámaras?", a: "Aún no. Lumfi se enfoca en cash. Tu contador maneja la amortización, Lumfi te dice si tienes para comprar el lente." },
      { q: "¿Maneja gastos del evento (asistente, locación)?", a: "Sí. Cada evento puede tener gastos directos asociados que Lumfi descuenta del ingreso bruto." },
    ],
    cta: "Repartir mi temporada con Lumfi",
  },
];

// English mirror — concise, professionally translated. Same shape as ES.
const EN = ES.map((a) => {
  const en = {
    developer: {
      who: "Freelance developer",
      tag: "Cross-currency",
      heroTitle: "Bill in USD. Live in local currency. See your real cash without juggling three apps.",
      heroSub: "If you bill clients abroad and pay rent, cards and suppliers in local currency, your real cash changes every day. Lumfi tells you the number.",
      pains: [
        { title: "Your “balance” lies", body: "The bank shows one number, but between FX, taxes and pending receivables, you don't know what's really yours." },
        { title: "Excel + Notion + 3 accounts", body: "You spend Monday morning consolidating how many USD are coming and at what rate. By the time you're done, it's Tuesday." },
        { title: "Client pays, bank delays", body: "Wise, Payoneer, direct transfer — each on its own clock. Your flow gets distorted and you only notice when it's too late." },
      ],
      gains: [
        { title: "One number, two currencies", body: "Lumfi shows your cash in USD and local currency with live FX." },
        { title: "Receivables with probability", body: "Each receivable carries a confidence %. Lumfi weights it and tells you what you can really count on this month." },
        { title: "Telegram bot to log", body: "“Got 3,500 USD from client X.” Done. Conversion, account and category filled." },
      ],
      cta: "Try Lumfi with my real cash",
    },
    designer: {
      who: "UX/UI designer",
      tag: "Long projects",
      heroTitle: "Upfront, milestone, closing. Live without the rollercoaster between payments.",
      heroSub: "Your projects last 2-4 months. The month you collect the closing payment you feel rich, the next you drown. Lumfi spreads your mental cash so it stops swinging.",
      pains: [
        { title: "Fat month / thin month", body: "You collected 12K Friday. Do you spend it or is that money already booked for the next two months?" },
        { title: "Advances in limbo", body: "Contract signed but the upfront still hasn't landed. Should you plan around that money or not?" },
        { title: "Endless revisions", body: "The project stretched another month. The client pays at the end. Your cash calendar just broke." },
      ],
      gains: [
        { title: "Cash spread weekly", body: "Lumfi divides your incoming payments across the weeks ahead and gives you an honest weekly safe limit." },
        { title: "Milestones as recurring receivables", body: "Load the contract as 3 payments (upfront, mid, closing) and Lumfi projects each with probability." },
        { title: "Alerts when you're over", body: "When the project slips or you overspend, Lumfi warns you in time." },
      ],
      cta: "Split my next project in Lumfi",
    },
    "content-creator": {
      who: "Content creator",
      tag: "Multi-source",
      heroTitle: "AdSense, brands, subs, events. One number you understand.",
      heroSub: "Every platform pays a different day, in a different currency, with different withholdings. Lumfi unifies your real cash without open tabs.",
      pains: [
        { title: "5 sources, 5 calendars", body: "YouTube pays the 21st. Brand pays Net 60. Patreon is weekly. You lose track." },
        { title: "Unpredictable brand deals", body: "Three signed this month, none next. How do you spread that cash?" },
        { title: "Withholdings & taxes", body: "AdSense holds 30%. Brands sometimes with invoice, sometimes not. You don't know what's really yours." },
      ],
      gains: [
        { title: "All sources consolidated", body: "AdSense, brands, Patreon, events. Each with its payday, currency, withholding." },
        { title: "Visual receivables calendar", body: "Look month-by-month at what's coming in and how likely." },
        { title: "Weekly WhatsApp digest", body: "Every Monday Lumfi messages you: this week's cash, what's due, what's overdue." },
      ],
      cta: "Unify my sources in Lumfi",
    },
    consultant: {
      who: "Independent consultant",
      tag: "Retainer + spot",
      heroTitle: "Retainer + projects. What happens when the retainer ends?",
      heroSub: "You live off a stable monthly retainer plus side projects. But the day the retainer client leaves, how much runway do you have? Lumfi tells you upfront.",
      pains: [
        { title: "The retainer is your floor", body: "You take it for granted. But that client is one email away from leaving." },
        { title: "Spot projects are random", body: "You take them because they come, not because you need them. Then they pile up in the same month." },
        { title: "No numeric Plan B", body: "You know you'd be fine “a couple of months”. But exactly how many?" },
      ],
      gains: [
        { title: "What-if scenarios", body: "“What if I lose the retainer in March?” Lumfi shows the next 90 days without that cash." },
        { title: "Recurring vs spot", body: "Separate both. Know which is stable income and which is bonus." },
        { title: "Accountant-ready exports", body: "Export by project, month or client. No middle Excel." },
      ],
      cta: "Run a scenario in Lumfi",
    },
    "lawyer-accountant": {
      who: "Lawyer or accountant",
      tag: "Cyclical",
      heroTitle: "Cyclical fees. Fat months and thin months. Zero surprises.",
      heroSub: "Clients pay at month-end, at the close of the quarter or when the court releases fees. Lumfi teaches you to live the thin month with the fat month's money.",
      pains: [
        { title: "Bimonthly or quarterly receipts", body: "Two months without income, then it all lands at once. Hard to spread." },
        { title: "Unpredictable court fees", body: "Comes out when it comes out. You have to live as if it might never come." },
        { title: "High fixed costs", body: "Office, assistant, legal subs. Cash must always cover them, not “sometimes”." },
      ],
      gains: [
        { title: "90-day forecast", body: "See the thin month before it arrives. Lumfi tells you how much of today's payment to save." },
        { title: "Seasonal receivables", body: "Mark a fee as “probable Q2” without an exact date and Lumfi projects it with probability." },
        { title: "Reconciliation exports", body: "Ready for your accountant or partner. By client, by file, by month." },
      ],
      cta: "Forecast my fees with Lumfi",
    },
    "coach-tutor": {
      who: "Online coach or tutor",
      tag: "Subscriptions",
      heroTitle: "Subs that come and go. See your real MRR, not the gross.",
      heroSub: "You sell monthly subs or packages. People sign up, cancel, come back. Lumfi shows the actual recurring income, not the mirage.",
      pains: [
        { title: "Silent churn", body: "Three clients canceled this month but you didn't notice until close." },
        { title: "MRR vs. real income", body: "Stripe shows MRR but doesn't discount card fees, VAT, refunds. Your real cash is different." },
        { title: "Packages vs. monthlies", body: "You mix annual packages with monthlies. Tracking the flow is painful." },
      ],
      gains: [
        { title: "Real projected MRR", body: "Lumfi sums active subscriptions, discounts fees and FX, gives you the number that counts." },
        { title: "Retention metrics", body: "See how long clients last on average and predict your churn." },
        { title: "Churn alerts", body: "When a client cancels, Lumfi warns you and recalculates the next 3 months." },
      ],
      cta: "Compute my real MRR with Lumfi",
    },
    ecommerce: {
      who: "Solo e-commerce",
      tag: "Inventory gap",
      heroTitle: "Mercado Libre releases day after tomorrow. Your supplier wants payment today. How do you square it?",
      heroSub: "You get paid when ML/Shopify release. But the supplier asks for half before. That timing gap can break you. Lumfi makes it visible.",
      pains: [
        { title: "Receipts with hold", body: "ML releases 14 days later. How much do you have today vs. committed?" },
        { title: "Suppliers before sales", body: "You pay stock before selling it. Misjudge and you're cashless by mid-month." },
        { title: "Multiple platforms", body: "ML + Shopify + brick & mortar + Instagram. Each with its account, fee, day." },
      ],
      gains: [
        { title: "Accounts per platform", body: "Lumfi shows cash for each channel separately and the consolidated total." },
        { title: "Committed future payments", body: "Supplier asks at 30 days: Lumfi discounts it before showing your safe-to-use." },
        { title: "Weekly cash gap", body: "See if next week runs out of oxygen before it happens." },
      ],
      cta: "Close my gap with Lumfi",
    },
    photographer: {
      who: "Photographer / events",
      heroTitle: "Your season is 5 months. You have to live 12 on that cash.",
      heroSub: "Weddings & events live Nov-Mar. Apr-Oct you're at minimums. Lumfi shows you how much to set aside and when to spend it.",
      pains: [
        { title: "High season fools you", body: "You cash three weddings in December and feel like a king. Then May arrives." },
        { title: "Gear costs money", body: "Lenses, cameras, drones, parts. You invest in high season so it pays off in high season… if you have anything left." },
        { title: "Upfront vs. closing", body: "Every event is 30% on signing + 70% on event day. Projecting that without Excel is hard." },
      ],
      gains: [
        { title: "Split across low months", body: "Lumfi calculates how much to set aside from each event so you can live the 7 quiet months." },
        { title: "Auto tax reserve", body: "On each large receipt, Lumfi withholds the tax %. Come April you don't cry." },
        { title: "Seasonal saving goals", body: "Set how much you need to reach October. Lumfi tells you if you're on track." },
      ],
      cta: "Split my season with Lumfi",
    },
  };
  const t = en[a.slug] || {};
  return {
    ...a,
    who: t.who || a.who,
    tag: t.tag || a.tag,
    heroTitle: t.heroTitle || a.heroTitle,
    heroSub: t.heroSub || a.heroSub,
    pains: t.pains || a.pains,
    gains: t.gains || a.gains,
    cta: t.cta || a.cta,
    // Features, workflow, quote, faqs left in ES with TODO: full EN translation
    // when the freelancer EN audience grows past validation.
  };
});

// Portuguese reuses Spanish for now (placeholder convention from
// LandingArchetypes.jsx). Frozen so any future divergence stays explicit
// and doesn't accidentally mutate the ES source.
const PT = Object.freeze(ES.map((a) => ({ ...a })));

export const ARCHETYPE_CONTENT = Object.freeze({
  es: Object.freeze(ES),
  en: Object.freeze(EN),
  pt: PT,
});

export function getArchetype(lang, slug) {
  const set = ARCHETYPE_CONTENT[lang] || ARCHETYPE_CONTENT.es;
  return set.find((a) => a.slug === slug) || null;
}

export function getAllArchetypes(lang) {
  return ARCHETYPE_CONTENT[lang] || ARCHETYPE_CONTENT.es;
}
